import { X } from "lucide-react"

import type { ProductImageDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"

import type { ProductFormLabels } from "./product-field-types"

interface ExistingProductAssetsProps {
  labels: ProductFormLabels
  existingImages: ProductImageDto[]
  existingTechSpec?: string
  onDeleteImage?: (id: number) => void
}

export function ExistingProductAssets({
  labels,
  existingImages,
  existingTechSpec,
  onDeleteImage,
}: ExistingProductAssetsProps) {
  if (existingImages.length === 0 && !existingTechSpec) return null

  return (
    <div className="mb-10 grid grid-cols-1 gap-8 rounded-lg border-2 border-dashed border-border/50 bg-muted/5 p-8 md:grid-cols-2">
      {existingImages.length > 0 && (
        <div className="space-y-4">
          <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground/60">
            {labels.currentImages}
          </Label>
          <div className="flex flex-wrap gap-3">
            {existingImages.map((image) => (
              <div
                key={image.id}
                className="group relative h-20 w-20 overflow-hidden rounded-lg border-2 border-border transition-all hover:border-primary"
              >
                <img
                  src={apiClient.ensureAbsUrl(image.fileUrl)}
                  alt="Existing"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {onDeleteImage && (
                  <button
                    type="button"
                    onClick={() => onDeleteImage(image.id)}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md bg-destructive text-destructive-foreground opacity-0 shadow-xl transition-opacity group-hover:opacity-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {existingTechSpec && (
        <div className="space-y-4">
          <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground/60">
            {labels.currentTechSpec}
          </Label>
          <div className="flex items-center gap-4 rounded-lg border border-border bg-background p-4 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <X className="h-5 w-5 rotate-45" />
            </div>
            <div className="flex-1">
              <p className="font-display text-lg leading-none tracking-widest">SPECIFICATION_PDF</p>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs font-bold text-accent"
                onClick={() => window.open(apiClient.ensureAbsUrl(existingTechSpec), "_blank")}
              >
                {labels.viewDocument}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
